import React from "react";
import { Chart } from "primereact/chart";
import type { ChartData, ChartOptions } from "chart.js";
import type { Ratio } from "../../../../types/targetGroupAnalytics";
import { COCHARGE_DARK_GREEN, SURFACE_BORDER } from "../../../../styles/theme_constants";

interface GaugeWidgetProps {
  title: string;
  ratio: Ratio;
}

/**
 * Percentage gauge backed by a Ratio. Renders numerator/denominator (FR-006)
 * and, when the denominator is unavailable, shows that state instead of a
 * misleading percentage (FR-007).
 */
const GaugeWidget: React.FC<GaugeWidgetProps> = ({ title, ratio }) => {
  const denomUnavailable = ratio.denominator === null;
  const denom = ratio.denominator ?? 0;
  const pct = !denomUnavailable && denom > 0 ? (ratio.numerator / denom) * 100 : null;

  const data: ChartData<"doughnut"> = {
    labels: ["Used", "Remaining"],
    datasets: [
      {
        data:
          denomUnavailable || denom <= 0
            ? [0, 1]
            : [ratio.numerator, Math.max(denom - ratio.numerator, 0)],
        backgroundColor: [COCHARGE_DARK_GREEN, SURFACE_BORDER],
        borderWidth: 0,
      },
    ],
  };

  const options: ChartOptions<"doughnut"> = {
    cutout: "72%",
    animation: { duration: 0 },
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: !denomUnavailable && denom > 0 },
    },
  };

  const fmt = (n: number) => n.toLocaleString("de-DE");

  return (
    <div className="flex flex-column align-items-center text-center p-2" data-testid="gauge-widget">
      <span
        className="text-color-secondary text-xs font-semibold uppercase mb-2"
        style={{ letterSpacing: "0.05em" }}
      >
        {title}
      </span>
      <div style={{ position: "relative", width: 160, height: 160 }}>
        <Chart
          type="doughnut"
          data={data}
          options={options}
          style={{ width: 160, height: 160 }}
        />
        <div
          className="flex flex-column align-items-center justify-content-center"
          style={{ position: "absolute", inset: 0 }}
        >
          {denomUnavailable ? (
            <span className="text-xs font-semibold text-color-secondary px-2">
              denominator
              <br />
              unavailable
            </span>
          ) : (
            <span className="text-2xl font-bold text-color">
              {pct === null ? "—" : `${pct.toFixed(0)}%`}
            </span>
          )}
        </div>
      </div>
      <span className="text-sm text-color-secondary mt-2">
        {denomUnavailable
          ? `${fmt(ratio.numerator)} / denominator unavailable`
          : `${fmt(ratio.numerator)} / ${fmt(denom)}`}
      </span>
    </div>
  );
};

export default GaugeWidget;
